// Libs
import { parsePhoneNumberFromString } from 'libphonenumber-js/max'

/**
 * Sentinel for "no comparable mobile number", outside the sanitizer's output space.
 * @type {String}
 */
export const NO_MOBILE_NO = '[no-mobile-no]'

/**
 * Sanitize phone-number input.
 * @param {*} input - Raw input, which Excel yields as a Number for numeric cells
 * @returns {String} Digits, with a leading `+` preserved
 */
export function sanitize(input) {
  if (input === null || input === undefined) return ''

  const value = String(input).trim()
  const prefix = value.charAt(0) === '+' ? '+' : ''

  return prefix + value.replace(/\D/g, '')
}

/**
 * Format an API-sourced phone number for display, in the format the API provides.
 * @param {*} input - API-sourced value
 * @returns {String|null} Display value, or null when there is nothing to show; render sites depend on that null
 */
export function display(input) {
  if (typeof input !== 'string' || !input) return null

  // NOTE: correct a legacy bare-66 value that predates ADR 0001, for display only.
  if (/^66[689]\d{8}$/.test(input)) return input.replace(/^66/, '0')

  return input
}

// Parse a sanitized value; a leading `+` is the only signal to skip the Thailand assumption.
function parse(value) {
  if (value.charAt(0) === '+') return parsePhoneNumberFromString(value)

  // NOTE: this guard retires the bare `66xxxxxxxxx` shape and a zero-dropped `812345678`.
  if (!/^0\d{9}$/.test(value)) return undefined

  return parsePhoneNumberFromString(value, 'TH')
}

// Thai labels for international line types that are parseable but rejected as non-mobile.
const TYPE_TH = {
  FIXED_LINE      : 'เบอร์บ้าน/เบอร์คงที่',
  VOIP            : 'เบอร์ VOIP',
  PAGER           : 'เบอร์เพจเจอร์',
  PREMIUM_RATE    : 'เบอร์เก็บค่าบริการพิเศษ',
  TOLL_FREE       : 'เบอร์โทรฟรี',
  SHARED_COST     : 'เบอร์ร่วมจ่ายค่าโทร',
  PERSONAL_NUMBER : 'เบอร์ส่วนบุคคล',
  UAN             : 'เบอร์ UAN',
  VOICEMAIL       : 'เบอร์ฝากข้อความเสียง',
}

/**
 * Explain why newly entered phone-number input fails validation.
 * @param {*} input - Raw input
 * @returns {String|null} Thai reason, or null when the input passes the Domestic or International rule
 */
export function invalidReason(input) {
  const value = sanitize(input)
  if (!value) return 'ยังไม่ได้กรอกเบอร์มือถือ'

  const parsed = parse(value)

  if (!parsed) return 'จำนวนหลักไม่ถูกต้อง'

  // NOTE: `0012781000` passes the domestic-shape guard but parses to South Africa via the `00` IDD prefix.
  if (value.charAt(0) !== '+' && parsed.country !== 'TH') {
    return 'รูปแบบเบอร์ไม่ใช่เบอร์ไทย'
  }

  // NOTE: ADR 0002 gates Domestic on this shape because `getType()` reports VOIP for Thai `06x`.
  if (parsed.country === 'TH') {
    return /^[689]\d{8}$/.test(parsed.nationalNumber)
      ? null
      : 'ไม่ใช่เบอร์มือถือ'
  }

  // International requires a mobile line (overrides ADR 0002's "any line type" stance,
  // per explicit product decision: this is a mobileNo field). FIXED_LINE_OR_MOBILE is
  // included because numbering plans like NANP (+1) can't distinguish mobile from
  // fixed-line at all, so treating it as reject-everything-ambiguous would wrongly
  // reject real US/Canada mobile numbers.
  const type = parsed.getType()
  if (type === 'MOBILE' || type === 'FIXED_LINE_OR_MOBILE') return null

  return `เป็น${TYPE_TH[type] || 'เบอร์ประเภทอื่นๆ'} ไม่ใช่เบอร์มือถือ`
}

/**
 * Validate newly entered phone-number input.
 * @param {*} input - Raw input
 * @returns {Boolean} Whether the input passes the Domestic or the International rule
 */
export function isValidNewInput(input) {
  return invalidReason(input) === null
}

/**
 * Normalize valid input to the E.164 value a payload carries.
 * @param {*} input - Raw input
 * @returns {String|null} E.164 value from the library, or null when the input is invalid
 */
export function normalize(input) {
  const value = sanitize(input)
  if (!isValidNewInput(value)) return null

  return parse(value).number
}

/**
 * Normalize a phone number for within-batch duplicate comparison.
 * @param {*} input - Raw input
 * @returns {String} E.164 value, or the truthy `NO_MOBILE_NO` sentinel; guard with `isValidNewInput`, or two invalid rows compare equal
 */
export function comparisonNormalize(input) {
  return normalize(input) || NO_MOBILE_NO
}
